import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper, getCoreRowModel, getSortedRowModel, getPaginationRowModel, useReactTable,
} from '@tanstack/react-table';
import { s, authHeaders, Toast, newRecordButtonStyle, getODataRows } from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import { C360Table, ColumnChooser, EditIcon, DeleteIcon } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/criticalskus';

const unwrap = (body) => body?.data ?? body?.Data ?? null;
const isoToDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('tr-TR');
};

export function CriticalSkuPage() {
  usePageMeta('Kritik SKU', 'Kriterlere uyan noktalarda tablette vurgulanacak ve satılmadığında uyarı verecek kritik ürünler.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [additionalFields, setAdditionalFields] = useState([]);

  const [listFilterInput, setListFilterInput] = useState('');
  const [listFilter, setListFilter] = useState('');
  const [listSelected, setListSelected] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  useEffect(() => {
    fetch('/api/customeradditionalfields?$top=500&$orderby=Name', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => setAdditionalFields(getODataRows(d)))
      .catch(() => {});
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/Select`, { headers: authHeaders() });
      const body = res.ok ? await res.json() : {};
      setRows(unwrap(body) || []);
    } catch {
      showToast('error', 'Kayıtlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleDelete = async (row) => {
    try {
      await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: row.id }) });
      showToast('success', 'Kritik SKU silindi');
      setDeleteConfirm(null);
      fetchRows();
    } catch {
      showToast('error', 'Silme başarısız');
      setDeleteConfirm(null);
    }
  };

  const fieldName = useMemo(() => {
    const map = new Map(additionalFields.map((f) => [f.id, f.name || f.Name]));
    return (id) => (id ? map.get(id) || '' : '');
  }, [additionalFields]);

  const criteriaSentence = (r) => {
    const parts = [];
    if (r.startDate || r.endDate) parts.push(`Tarih: ${isoToDate(r.startDate) || '…'} → ${isoToDate(r.endDate) || '…'}`);
    if (r.representativeId) parts.push('Temsilci');
    if (r.customerGroupId) parts.push('Grup');
    if (r.customerSubGroupId) parts.push('Ek Grup');
    if (r.customerDynamicGroupId) parts.push('Özel Grup');
    if (r.additionalFieldId) parts.push(`Saha: ${fieldName(r.additionalFieldId) || '—'}${r.additionalFieldValue ? `=${r.additionalFieldValue}` : ''}`);
    if (r.customerId) parts.push('Tekil nokta');
    return parts.length ? parts.join(' · ') : 'Tüm noktalar';
  };

  const filteredRows = useMemo(() => {
    const q = listFilter.trim().toLocaleLowerCase('tr-TR');
    if (!q) return rows;
    return rows.filter((r) =>
      (r.textId || '').toLocaleLowerCase('tr-TR').includes(q) ||
      (r.description || '').toLocaleLowerCase('tr-TR').includes(q));
  }, [rows, listFilter]);

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('textId', {
      header: 'Kod',
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('description', { header: 'Açıklama', cell: (info) => info.getValue() || '-' }),
    columnHelper.display({
      id: 'criteria', header: 'Kriterler',
      cell: ({ row }) => <span style={{ color: 'var(--text-secondary)' }}>{criteriaSentence(row.original)}</span>,
    }),
    columnHelper.accessor('productCount', {
      header: 'Ürün',
      cell: (info) => <span className="c360-erp-chip">{info.getValue() ?? 0}</span>,
    }),
    columnHelper.accessor('isActive', {
      header: 'Durum',
      cell: (info) => (info.getValue() ? <StatusBadge tone="ok">Aktif</StatusBadge> : <StatusBadge tone="bad">Pasif</StatusBadge>),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Düzenle" aria-label="Düzenle" onClick={(e) => { e.stopPropagation(); navigate(`/critical-skus/${row.original.id}?edit=1`); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(row.original); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [fieldName, navigate, canEdit, canDelete]);

  const listTable = useReactTable({
    data: filteredRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex, pageSize } },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;
      setPageIndex(next.pageIndex); setPageSize(next.pageSize);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const listToolbar = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kod veya açıklama ile filtrele..."
        value={listFilterInput}
        onChange={(e) => setListFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { setListFilter(listFilterInput); setPageIndex(0); } }}
      />
      {listFilter && <button style={s.btnSecondary} onClick={() => { setListFilterInput(''); setListFilter(''); setPageIndex(0); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchRows}>Yenile</button>
      <ColumnChooser table={listTable} />
    </>
  );

  const listFooter = (
    <Pagination
      currentPage={pageIndex + 1}
      totalCount={filteredRows.length}
      pageSize={pageSize}
      rowCount={listTable.getRowModel().rows.length}
      onPageChange={(p) => setPageIndex(p - 1)}
      onPageSizeChange={(sz) => { setPageSize(sz); setPageIndex(0); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Kritik SKU"
        subtitle="Kriterlere uyan noktalarda tablette vurgulanacak ve satılmadığında uyarı verecek kritik ürünler."
        actions={canAdd && <button style={newRecordButtonStyle} onClick={() => navigate('/critical-skus/new')}>+ Yeni Kritik SKU</button>}
        toolbar={listToolbar}
        footer={listFooter}
      >
        <C360Table
          table={listTable}
          loading={loading}
          selected={listSelected}
          onRowClick={setListSelected}
          onRowDoubleClick={(row) => navigate(`/critical-skus/${row.id}`)}
          emptyText="Henüz kritik SKU tanımlanmadı. “+ Yeni Kritik SKU” ile başlayın."
        />
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, lineHeight: 1.6, color: 'var(--text-primary)' }}>
                <strong>"{deleteConfirm.textId}"</strong> kritik SKU tanımını ve ürünlerini silmek istediğinize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={() => handleDelete(deleteConfirm)}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(null)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default CriticalSkuPage;
